import React, { useState, useEffect, useRef } from 'react';
import Lenis from '@studio-freight/lenis';
import './picklePointTheme.css';
import OpenMapsLink from "./OpenMapsLink";
import heroImg    from './picklepointnepal-assets/pickle-point-nepal.webp';
import ground1Img from './picklepointnepal-assets/pickle-point-nepal-g1.webp';
import ground2Img from './picklepointnepal-assets/pickle-point-nepal-g2.webp';
import appMockup  from './picklepointnepal-assets/app-mockup.png';
import ongroundLogo from '../assets/branding/onground-powered-by-dark-bg.png';
import OngroundStoreIcon from '../OngroundStoreIcon';
import {
  resolveOngroundAppLink,
  resolveVenueSocialLink,
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
  resolveThemeObject,
} from '../publicWebsiteContent';
import { resolveThemePaletteStyle } from '../themePalette';
import { isWebsiteSectionVisible } from '../themeDefinitions';
import usePublicContactEnquiry from '../usePublicContactEnquiry';
import PublicContactEnquiryStatus, { PublicContactHoneypot, PublicContactPhoneField } from '../PublicContactEnquiryStatus';


// ── Data ──────────────────────────────────────────────────────────────────────
const STATIC_COURTS = [
  {
    id: 1, img: ground1Img, name: 'Pickle Court 1', sport: 'Pickleball',
    price: 'NPR 1,000/hr', badge: 'Court 1',
    desc: 'Our flagship court with premium surface, full lighting and professional nets — ideal for competitive play.',
  },
  {
    id: 2, img: ground2Img, name: 'Pickle Court 2', sport: 'Pickleball',
    price: 'NPR 1,000/hr', badge: 'Court 2',
    desc: 'Spacious covered court with great visibility. Perfect for group sessions and beginners learning the game.',
  },
];

const STATIC_STATS = [
  { n: 2, suffix: '', label: 'Premium Courts' },
  { n: 1000, suffix: '+', label: 'Players / Month' },
  { n: 16, suffix: ' hrs', label: 'Open Daily' },
  { n: 1000, suffix: '/hr', label: 'Starting Price (NPR)' },
];

const STATIC_AMENITIES = [
  { icon: '🅿️', label: 'Free Parking' },
  { icon: '🚿', label: 'Washroom' },
  { icon: '👕', label: 'Changing Room' },
  { icon: '💡', label: 'Floodlights' },
  { icon: '🏓', label: 'Paddle Rental' },
  { icon: '🎾', label: 'Ball Sales' },
];

const STATIC_TESTIMONIALS = [
  {
    text: 'Easily the best pickleball experience in Kathmandu. The courts are clean, well-lit, and the community here is amazing. I bring all my friends.',
    name: 'Samita Pradhan', sport: 'Regular Player', rating: 5,
  },
  {
    text: 'I started pickleball at Pickle Point and I was completely hooked within a week. The staff are super welcoming and the vibe is just incredible.',
    name: 'Rohan Malla', sport: 'Beginner Player', rating: 5,
  },
  {
    text: 'We host our monthly office pickleball day here. The covered courts mean rain never spoils our fun. Highly recommend for corporate bookings!',
    name: 'Priya KC', sport: 'Corporate Events', rating: 5,
  },
];

const STATIC_FAQS = [
  { q: 'How do I book a court?', a: 'You can book via the ONGROUND app 24/7, or call us directly at +9779700039241. Walk-ins are also welcome based on availability.' },
  { q: 'Can I rent equipment?', a: 'Absolutely! We offer paddle rental at NPR 100 per paddle, and balls are available for sale at NPR 150 each.' },
  { q: 'What are the opening hours?', a: 'We are open daily from 6:00 AM to 10:00 PM, including weekends and public holidays.' },
  { q: 'Do you offer coaching or training?', a: 'Yes! We have certified pickleball coaches available for private and group sessions. Contact us to schedule a session.' },
  { q: 'Is the venue suitable for beginners?', a: 'Absolutely — Pickle Point is perfect for beginners and regular players alike. Our open sessions are a great way to learn and meet the community.' },
  { q: 'What is the cancellation policy?', a: 'Free cancellation up to 3 hours before your slot via the ONGROUND app. Late cancellations may incur a partial charge.' },
];

const STATIC_MARQUEE_WORDS = [
  'SERVE', 'DINK', 'SMASH', 'RALLY', 'PLAY', 'REPEAT',
  'SERVE', 'DINK', 'SMASH', 'RALLY', 'PLAY', 'REPEAT',
];

const PICKLE_POINT_THEME_ID = 'pickle-point-nepal';

const FACILITY_PRESENTATION = Object.freeze({
  parking: { icon: '🅿️', label: 'Free Parking' },
  washroom: { icon: '🚿', label: 'Washroom' },
  changing_room: { icon: '👕', label: 'Changing Room' },
  lights: { icon: '💡', label: 'Floodlights' },
  cafeteria: { icon: '☕', label: 'Cafeteria' },
  equipment_rental: { icon: '🏓', label: 'Paddle Rental' },
});

const cleanText = (value) =>
  String(value == null ? '' : value).trim();

const splitVenueBrand = (venueName) => {
  const parts = cleanText(venueName)
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return {
      primary: 'Pickle',
      highlighted: 'Point',
    };
  }

  return {
    primary: parts[0],
    highlighted: parts.slice(1).join(' '),
  };
};

const formatLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatTime = (value, fallback) => {
  const input = cleanText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input || fallback;
  }

  const [hourText, minute] = input.split(':');
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const getOpenHoursDuration = (openTime, closeTime) => {
  const parseMinutes = (value) => {
    const match = cleanText(value).match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  };

  const start = parseMinutes(openTime);
  const end = parseMinutes(closeTime);

  if (start == null || end == null || end <= start) {
    return null;
  }

  return Math.round((end - start) / 60);
};

const getVenuePhone = (venue) =>
  cleanText(venue?.contact?.e164) ||
  cleanText(venue?.contact?.nationalNumber) ||
  '';

const getVenueAddress = (venue) => {
  const address = cleanText(venue?.location?.address);
  if (address) return address;

  return [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ].filter(Boolean).join(', ');
};

const getSafeExternalUrl = (value, fallback = '#') => {
  const input = cleanText(value);

  if (!input || input === '#') {
    return fallback;
  }

  try {
    const parsed = new URL(input);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return fallback;
    }

    return parsed.toString();
  } catch {
    return fallback;
  }
};

const buildMapEmbedUrl = (venue, configuredUrl) => {
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ''
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ''
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (hasValidCoordinates) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}&z=15&output=embed`;
  }

  const safeConfiguredUrl = getSafeExternalUrl(configuredUrl, '');
  if (safeConfiguredUrl) {
    return safeConfiguredUrl;
  }

  const address = getVenueAddress(venue) || 'Pulchowk,Lalitpur,Nepal';
  return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`;
};

const formatGroundPrice = (ground, venue) => {
  const amount = Number(
    ground?.basePrice ??
    venue?.pricing?.basePrice,
  );

  const currency = cleanText(
    ground?.currency ||
    venue?.pricing?.currency,
  ).toUpperCase();

  const unit = cleanText(ground?.unit).toLowerCase();

  if (!Number.isFinite(amount) || amount <= 0) {
    return '';
  }

  const formattedAmount = amount.toLocaleString('en-US');
  const unitSuffix = unit === 'slot' ? '/slot' : '/hr';

  return `${currency || 'NPR'} ${formattedAmount}${unitSuffix}`;
};

const getGroundImage = (ground) =>
  cleanText(
    ground?.mainImageUrl ||
    ground?.imageUrl,
  );

const normalizeRating = (value) => {
  const rating = Math.round(Number(value));
  return Number.isFinite(rating)
    ? Math.min(5, Math.max(1, rating))
    : 5;
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useScrollAnimation(disabled = false) {
  useEffect(() => {
    if (disabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('pp-in-view');
        });
      },
      { threshold: 0.1 }
    );
    const els = document.querySelectorAll('[data-animate]');
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [disabled]);
}

function useCounter(target, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);
  return count;
}

// ── StatCell ──────────────────────────────────────────────────────────────────
function StatCell({ n, suffix, label }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const count = useCounter(n, inView);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div className="pp-stat-cell" ref={ref}>
      <div className="pp-stat-num">{count.toLocaleString()}<span className="pp-stat-suffix">{suffix}</span></div>
      <div className="pp-stat-label">{label}</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PicklePointTheme({
  venue = {},
  grounds = [],
  website = {},
  primaryColor = "#FF5A3C",
  accentColor = "#181310",
  heroImage: commonHeroImage = "",
  openBooking = () => {},
  previewMode = false,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq]   = useState(null);
  const lenisRef = useRef(null);

  const themePaletteStyle = resolveThemePaletteStyle(
    PICKLE_POINT_THEME_ID,
    primaryColor,
    accentColor,
  );

  const t = (
    key,
    fallback,
    apiValue = '',
  ) =>
    resolveThemeCopy(
      website,
      PICKLE_POINT_THEME_ID,
      key,
      fallback,
      apiValue,
    );

  const m = (
    key,
    fallback,
  ) =>
    resolveThemeMedia(
      website,
      PICKLE_POINT_THEME_ID,
      key,
      fallback,
    );

  const {
    values: formData,
    handleChange: handleInput,
    submit: handleSubmit,
    submitting: contactSubmitting,
    feedback: contactFeedback,
  } = usePublicContactEnquiry({
    venue,
    previewMode,
    successMessage: t('formSuccessMessage', 'Message sent! We will get back to you shortly.'),
  });

  const social = resolveThemeObject(
    website,
    PICKLE_POINT_THEME_ID,
    'social',
    {},
  );

  const bookingEnabled = website?.booking?.enabled !== false;

  const appStoreUrl = resolveOngroundAppLink(
    social?.appStoreUrl,
    'appStore',
  );
  const googlePlayUrl = resolveOngroundAppLink(
    social?.googlePlayUrl,
    'googlePlay',
  );

  const instagramUrl = resolveVenueSocialLink(
    social?.instagramUrl,
    'instagram',
  );
  const facebookUrl = resolveVenueSocialLink(
    social?.facebookUrl,
    'facebook',
  );
  const tiktokUrl = resolveVenueSocialLink(
    social?.tiktokUrl,
    'tiktok',
  );
  const whatsappUrl = resolveVenueSocialLink(
    social?.whatsappUrl,
    'whatsapp',
  );

  const hasVenueSocialLinks =
    !!(instagramUrl || facebookUrl || tiktokUrl || whatsappUrl);

  const apiVenuePhone =
    getVenuePhone(venue);

  const venuePhone = t(
    'phoneValue',
    '+977 9700039241',
    apiVenuePhone,
  );

  const apiVenueAddress =
    getVenueAddress(venue);

  const venueAddress = t(
    'locationValue',
    'Pulchowk, Lalitpur, Nepal',
    apiVenueAddress,
  );

  const venueCity =
    cleanText(venue?.location?.city) ||
    'Pulchowk';

  const venueCountry =
    cleanText(
      venue?.location?.country ||
      venue?.country,
    ) ||
    'Nepal';

  const venueBrand = splitVenueBrand(
    cleanText(venue?.name) || 'Pickle Point',
  );

  const rawOpenTime =
    cleanText(venue?.policies?.openTime) ||
    '06:00';

  const rawCloseTime =
    cleanText(venue?.policies?.closeTime) ||
    '22:00';

  const openingTime = formatTime(
    rawOpenTime,
    '6:00 AM',
  );

  const closingTime = formatTime(
    rawCloseTime,
    '10:00 PM',
  );

  const openHoursDuration =
    getOpenHoursDuration(
      rawOpenTime,
      rawCloseTime,
    ) || 16;


  const hasApiHours =
    !!cleanText(venue?.policies?.openTime) ||
    !!cleanText(venue?.policies?.closeTime);

  const displayHours = t(
    'hoursValue',
    '6:00 AM – 10:00 PM',
    hasApiHours
      ? `${openingTime} – ${closingTime}`
      : '',
  );


  const apiHeroTag =
    apiVenueAddress || hasApiHours
      ? `${apiVenueAddress || 'Pulchowk, Lalitpur'} · Open ${
          hasApiHours
            ? `${openingTime} – ${closingTime}`
            : '6 AM – 10 PM'
        }`
      : '';

  const venueGrounds = Array.isArray(grounds)
    ? grounds
    : [];

  const minimumGroundPrice = venueGrounds
    .map((ground) => Number(ground?.basePrice))
    .filter((amount) => Number.isFinite(amount) && amount > 0)
    .sort((left, right) => left - right)[0];

  const venueBasePrice = Number(
    minimumGroundPrice ??
    venue?.pricing?.basePrice,
  );

  const venueCurrency = cleanText(
    venueGrounds.find(
      (ground) =>
        Number(ground?.basePrice) ===
        minimumGroundPrice,
    )?.currency ||
    venue?.pricing?.currency,
  ).toUpperCase() || 'NPR';

  const apiCourts = venueGrounds.map(
    (ground, index) => {
      const fallback =
        STATIC_COURTS[index] ||
        STATIC_COURTS[
          STATIC_COURTS.length - 1
        ];

      return {
        id:
          cleanText(ground?._id) ||
          `ground-${index + 1}`,
        img:
          getGroundImage(ground) ||
          fallback.img,
        name:
          cleanText(ground?.name) ||
          fallback.name,
        sport:
          formatLabel(
            ground?.sportName ||
            ground?.sport?.name ||
            ground?.type,
          ) ||
          fallback.sport,
        price:
          formatGroundPrice(ground, venue) ||
          fallback.price,
        badge:
          t(
            `courtBadge${index + 1}`,
            fallback.badge,
          ),
        desc:
          cleanText(ground?.description) ||
          fallback.desc,
        ground,
      };
    },
  );

  const courtSource =
    apiCourts.length > 0
      ? apiCourts
      : resolveThemeArray(
        website,
        PICKLE_POINT_THEME_ID,
        'courts',
        STATIC_COURTS,
        [],
      );

  const courtCards = courtSource.map((court, index) => {
    const fallback =
      STATIC_COURTS[index] ||
      STATIC_COURTS[
        STATIC_COURTS.length - 1
      ];

    return {
      id:
        cleanText(court?.id) ||
        `court-${index + 1}`,
      img:
        cleanText(court?.imageUrl || court?.img) ||
        fallback.img,
      name:
        cleanText(court?.name) ||
        fallback.name,
      sport:
        cleanText(court?.sport) ||
        fallback.sport,
      price:
        cleanText(court?.price) ||
        fallback.price,
      badge:
        cleanText(court?.badge) ||
        fallback.badge,
      desc:
        cleanText(court?.desc) ||
        fallback.desc,
      ground:
        court?.ground ||
        venueGrounds[index] ||
        null,
    };
  });

  const apiStats = [
    {
      n:
        venueGrounds.length ||
        STATIC_STATS[0].n,
      suffix: '',
      label: STATIC_STATS[0].label,
    },
    STATIC_STATS[1],
    {
      n: openHoursDuration,
      suffix: ' hrs',
      label: STATIC_STATS[2].label,
    },
    {
      n:
        Number.isFinite(venueBasePrice) &&
        venueBasePrice > 0
          ? venueBasePrice
          : STATIC_STATS[3].n,
      suffix: '/hr',
      label:
        Number.isFinite(venueBasePrice) &&
        venueBasePrice > 0
          ? `Starting Price (${venueCurrency})`
          : STATIC_STATS[3].label,
    },
  ];

  const stats = resolveThemeArray(
    website,
    PICKLE_POINT_THEME_ID,
    'stats',
    STATIC_STATS,
    apiStats,
  ).map((stat, index) => {
    const fallback =
      STATIC_STATS[index] ||
      STATIC_STATS[
        STATIC_STATS.length - 1
      ];

    const value = Number(
      stat?.n ??
      stat?.value,
    );

    return {
      n: Number.isFinite(value)
        ? value
        : fallback.n,
      suffix:
        cleanText(stat?.suffix) ||
        fallback.suffix,
      label:
        cleanText(stat?.label) ||
        fallback.label,
    };
  });

  const apiAmenities = Array.isArray(
    venue?.facilities,
  )
    ? venue.facilities
        .map((facility) => {
          const key = cleanText(facility);
          if (!key) return null;

          return (
            FACILITY_PRESENTATION[key] || {
              icon: '✓',
              label: formatLabel(key),
            }
          );
        })
        .filter(Boolean)
    : [];

  const amenities = resolveThemeArray(
    website,
    PICKLE_POINT_THEME_ID,
    'amenities',
    STATIC_AMENITIES,
    apiAmenities,
  ).map((amenity, index) => {
    const fallback =
      STATIC_AMENITIES[index] ||
      STATIC_AMENITIES[
        STATIC_AMENITIES.length - 1
      ];

    return {
      icon:
        cleanText(amenity?.icon) ||
        fallback.icon,
      label:
        cleanText(amenity?.label) ||
        fallback.label,
    };
  });

  const testimonials = resolveThemeArray(
    website,
    PICKLE_POINT_THEME_ID,
    'testimonials',
    STATIC_TESTIMONIALS,
  ).map((testimonial, index) => {
    const fallback =
      STATIC_TESTIMONIALS[index] ||
      STATIC_TESTIMONIALS[
        STATIC_TESTIMONIALS.length - 1
      ];

    return {
      text:
        cleanText(testimonial?.text) ||
        fallback.text,
      name:
        cleanText(testimonial?.name) ||
        fallback.name,
      sport:
        cleanText(
          testimonial?.sport ||
          testimonial?.role,
        ) ||
        fallback.sport,
      rating: normalizeRating(
        testimonial?.rating ??
        fallback.rating,
      ),
    };
  });

  const apiFaqs = STATIC_FAQS.map(
    (faq, index) => {
      if (index === 0 && getVenuePhone(venue)) {
        return {
          ...faq,
          a:
            `You can book via the ONGROUND app 24/7, or call us directly at ${venuePhone}. ` +
            'Walk-ins are also welcome based on availability.',
        };
      }

      if (
        index === 2 &&
        (
          cleanText(venue?.policies?.openTime) ||
          cleanText(venue?.policies?.closeTime)
        )
      ) {
        return {
          ...faq,
          a:
            `We are open daily from ${openingTime} to ${closingTime}, ` +
            'including weekends and public holidays.',
        };
      }

      return faq;
    },
  );

  const faqs = resolveThemeArray(
    website,
    PICKLE_POINT_THEME_ID,
    'faqs',
    STATIC_FAQS,
    apiFaqs,
  ).map((faq, index) => {
    const fallback =
      STATIC_FAQS[index] ||
      STATIC_FAQS[
        STATIC_FAQS.length - 1
      ];

    return {
      q:
        cleanText(
          faq?.q ||
          faq?.question,
        ) ||
        fallback.q,
      a:
        cleanText(
          faq?.a ||
          faq?.answer,
        ) ||
        fallback.a,
    };
  });

  const marqueeWords = resolveThemeArray(
    website,
    PICKLE_POINT_THEME_ID,
    'marqueeWords',
    STATIC_MARQUEE_WORDS.map(
      (word) => ({ word }),
    ),
  )
    .map((item) =>
      cleanText(
        typeof item === 'string'
          ? item
          : item?.word,
      ),
    )
    .filter(Boolean);

  const navigation = [
    {
      id: 'courts',
      sectionKey: 'grounds',
      label: t('navCourts', 'Courts'),
    },
    {
      id: 'about',
      sectionKey: 'about',
      label: t('navAbout', 'About'),
    },
    {
      id: 'faq',
      sectionKey: 'faqs',
      label: t('navFaq', 'FAQ'),
    },
    {
      id: 'contact',
      sectionKey: 'contact',
      label: t('navContact', 'Contact'),
    },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const heroImage = m(
    'heroImageUrl',
    cleanText(commonHeroImage) ||
    cleanText(
      venue?.media?.bannerImage,
    ) ||
    heroImg,
  );

  const aboutImage = m(
    'aboutImageUrl',
    getGroundImage(venueGrounds[1]) ||
    getGroundImage(venueGrounds[0]) ||
    cleanText(venue?.media?.gallery?.[0]) ||
    ground2Img,
  );

  const venueLogoUrl = resolveThemeLogo(
    website,
    PICKLE_POINT_THEME_ID,
    '',
  );

  const appImage = m(
    'appImageUrl',
    appMockup,
  );

  const poweredLogo = m(
    'poweredLogoUrl',
    ongroundLogo,
  );

  const mapEmbedUrl = buildMapEmbedUrl(
    venue,
    social?.mapEmbedUrl,
  );

  // Smooth scroll
// Smooth scroll
useEffect(() => {
  if (previewMode) return undefined;

  const prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  if (prefersReducedMotion) {
    lenisRef.current = null;
    return undefined;
  }

  const lenis = new Lenis({
    lerp: 0.12,
    smoothWheel: true,
    syncTouch: false,
    wheelMultiplier: 0.9,
    touchMultiplier: 1,
  });

  lenisRef.current = lenis;
  let animationFrameId = 0;

  const raf = (time) => {
    lenis.raf(time);
    animationFrameId = window.requestAnimationFrame(raf);
  };

  animationFrameId = window.requestAnimationFrame(raf);

  return () => {
    window.cancelAnimationFrame(animationFrameId);
    lenis.destroy();

    if (lenisRef.current === lenis) {
      lenisRef.current = null;
    }
  };
}, [previewMode]);

  // Navbar scroll
  useEffect(() => {
    if (previewMode) return undefined;

    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [previewMode]);

  useScrollAnimation(previewMode);

  const scrollTo = (id) => {
  if (previewMode) return;

  const element = document.getElementById(id);

  if (!element) {
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return;
  }

  const offset = id === 'hero' ? 0 : -88;
  const lenis = lenisRef.current;

  if (lenis) {
    lenis.scrollTo(element, {
      offset,
      lerp: 0.12,
    });
    return;
  }

  const top =
    element.getBoundingClientRect().top +
    window.scrollY +
    offset;

  const prefersReducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
};

  return (
    <div
      className={`pp-theme ${previewMode ? 'pp-theme-preview' : ''}`}
      style={themePaletteStyle}
    >

      {/* ── NAVBAR ── */}
      <header className={`pp-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="pp-nav-inner">
          <button
            className="pp-logo"
            onClick={() => scrollTo('hero')}
            aria-label={t(
              'logoAriaLabel',
              'Pickle Point Nepal',
              venue?.name,
            )}
          >
            {venueLogoUrl ? (
              <img
                className="pp-logo-image"
                src={venueLogoUrl}
                alt={`${venue?.name || 'Venue'} logo`}
              />
            ) : (
              <div className="pp-logo-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/>
                </svg>
              </div>
            )}
            <div>
              <span className="pp-logo-text">
                {t(
                  'logoPrimaryText',
                  venueBrand.primary,
                  venueBrand.primary,
                )}
                <span>
                  {t(
                    'logoHighlightText',
                    venueBrand.highlighted,
                    venueBrand.highlighted,
                  )}
                </span>
              </span>
              <span className="pp-logo-sub">
                {t(
                  'logoSubText',
                  'Nepal · Pulchowk',
                  `${venueCountry} · ${venueCity}`,
                )}
              </span>
            </div>
          </button>

          <nav className="pp-nav-links" aria-label="Main navigation">
            {navigation.map((link) => (
              <button
                key={link.id}
                className="pp-nav-link"
                onClick={() => scrollTo(link.id)}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {bookingEnabled && (
            <button className="pp-nav-cta" onClick={() => openBooking()}>
              {t('navCtaLabel', 'Book Now')}
            </button>
          )}
        </div>
      </header>

      {/* ── HERO ── */}
      {isWebsiteSectionVisible(website, "hero") && (
      <section id="hero" className="pp-hero">
        <div className="pp-hero-bg-shape" aria-hidden="true" />

        <div className="pp-hero-left">
          <div className="pp-hero-tag" data-animate="fade-right">
            <span className="pp-hero-tag-dot" />
            {t(
              'heroTag',
              'Pulchowk, Lalitpur · Open 6 AM – 10 PM',
              apiHeroTag,
            )}
          </div>

          <h1 className="pp-hero-title" data-animate="fade-right" style={{ '--delay': '0.1s' }}>
            {t('heroTitleLineOne', "Nepal's")}
            <span className="pp-hero-title-accent">
              {t('heroTitleHighlight', 'Premier')}
            </span>
            {t('heroTitleLineThree', 'Pickleball')}
          </h1>

          <p className="pp-hero-desc" data-animate="fade-right" style={{ '--delay': '0.2s' }}>
            {t(
              'heroDescription',
              "Play at Lalitpur's go-to pickleball destination. Fast, fun, and social — perfect for beginners and seasoned players alike. Two premium covered courts await you.",
              venue?.shortDescription,
            )}
          </p>

          <div className="pp-hero-actions" data-animate="fade-right" style={{ '--delay': '0.3s' }}>
            {isWebsiteSectionVisible(website, "grounds") && (
              <button className="pp-btn-primary pp-btn-lg" onClick={() => scrollTo('courts')}>
                {t('heroViewCourtsLabel', 'View Courts')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            )}
            {bookingEnabled && (
              <button className="pp-btn-outline pp-btn-lg" onClick={() => openBooking()}>
                {t('heroBookSlotLabel', 'Book a Slot')}
              </button>
            )}
          </div>

          <div className="pp-hero-meta" data-animate="fade-right" style={{ '--delay': '0.4s' }}>
            <div className="pp-hero-meta-item">
              <span className="pp-hero-meta-val">
                {t(
                  'heroCourtCountValue',
                  '2',
                  venueGrounds.length
                    ? String(venueGrounds.length)
                    : '',
                )}
              </span>
              <span className="pp-hero-meta-label">
                {t('heroCourtsLabel', 'Covered Courts')}
              </span>
            </div>
            <div className="pp-hero-meta-divider" />
            <div className="pp-hero-meta-item">
              <span className="pp-hero-meta-val">
                {t(
                  'heroPriceValue',
                  'NPR 1K',
                  Number.isFinite(venueBasePrice) && venueBasePrice > 0
                    ? `${venueCurrency} ${venueBasePrice.toLocaleString('en-US')}`
                    : '',
                )}
              </span>
              <span className="pp-hero-meta-label">
                {t('heroPriceLabel', 'Per Hour')}
              </span>
            </div>
            <div className="pp-hero-meta-divider" />
            <div className="pp-hero-meta-item">
              <span className="pp-hero-meta-val">
                {t(
                  'heroHoursValue',
                  '16 hrs',
                  hasApiHours
                    ? `${openHoursDuration} hrs`
                    : '',
                )}
              </span>
              <span className="pp-hero-meta-label">
                {t('heroHoursLabel', 'Open Daily')}
              </span>
            </div>
          </div>
        </div>

        <div className="pp-hero-right">
          <div className="pp-hero-img-wrap">
            <div
              className="pp-hero-img"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
            <div className="pp-hero-img-overlay" />
          </div>
          <div className="pp-hero-pill">
            <div className="pp-hero-pill-icon">{t('rentalIcon', '🏓')}</div>
            <div>
              <div className="pp-hero-pill-label">
                {t('rentalLabel', 'Paddle Rental')}
              </div>
              <div className="pp-hero-pill-sub">
                {t('rentalPriceText', 'NPR 100 · Balls NPR 150')}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── MARQUEE ── */}
      <div className="pp-marquee-section" aria-hidden="true">
        <div className="pp-marquee-track">
          <div className="pp-marquee-content">
            {marqueeWords.map((w, i) => (
              <span key={i} className="pp-marquee-item">
                {w}
                <span className="pp-marquee-dot" />
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── ABOUT ── */}
      {isWebsiteSectionVisible(website, "about") && (
      <section id="about" className="pp-section pp-bg-cream">
        <div className="pp-container">
          <div className="pp-about-grid">
            <div className="pp-about-image-wrap" data-animate="fade-right">
              <div
                className="pp-about-image"
                style={{ backgroundImage: `url(${aboutImage})` }}
              />
              <div className="pp-about-badge">
                <div className="pp-about-badge-spin">
                  <span className="pp-about-badge-num">
                    {t(
                      'aboutCourtCountValue',
                      '2',
                      venueGrounds.length
                        ? String(venueGrounds.length)
                        : '',
                    )}
                  </span>
                  <span className="pp-about-badge-text">
                    {t('aboutBadgeText', 'Premium Courts')}
                  </span>
                </div>
              </div>
            </div>

            <div data-animate="fade-left">
              <p className="pp-eyebrow">
                {t('aboutEyebrow', 'About The Venue')}
              </p>
              <h2 className="pp-section-title">
                {t(
                  'aboutTitle',
                  'Pickleball. Community. Passion.',
                )}
              </h2>
              <p className="pp-about-desc">
                {t(
                  'aboutParagraphOne',
                  "Pickle Point Nepal is Lalitpur's dedicated pickleball destination at Pulchowk. We built this venue for one purpose — giving Nepal's fastest-growing sports community a premium home to play, train, and connect.",
                  venue?.fullDescription,
                )}
              </p>
              <p className="pp-about-desc" style={{ marginTop: 0 }}>
                {t(
                  'aboutParagraphTwo',
                  "Whether you're picking up a paddle for the first time or chasing competitive glory, our two covered courts and welcoming community are ready for you.",
                  venue?.shortDescription,
                )}
              </p>

              <h3 style={{ fontFamily: 'var(--pp-font-head)', fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em', marginBottom: '16px', color: 'var(--pp-charcoal)' }}>
                {t('amenitiesTitle', 'Venue Amenities')}
              </h3>
              <div className="pp-amenities-grid">
                {amenities.map((a) => (
                  <div key={a.label} className="pp-amenity-chip">
                    <span className="pp-amenity-icon">{a.icon}</span>
                    {a.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── STATS ── */}
      {isWebsiteSectionVisible(website, "stats") && (
      <section className="pp-stats-section">
        <div className="pp-container">
          <div className="pp-stats-grid">
            {stats.map((s, index) => (
              <StatCell key={`${s.label}-${index}`} {...s} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── COURTS ── */}
      {isWebsiteSectionVisible(website, "grounds") && (
      <section id="courts" className="pp-courts-section">
        <div className="pp-container">
          <div className="pp-courts-header">
            <div data-animate="fade-right">
              <p className="pp-eyebrow">
                {t('courtsEyebrow', 'Available Courts')}
              </p>
              <h2 className="pp-section-title">
                {t('courtsTitle', 'Pick Your Court')}
              </h2>
            </div>
            <div className="pp-courts-scroll-hint" data-animate="fade-left">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              {t('courtsScrollHint', 'Scroll to explore')}
            </div>
          </div>
        </div>
        <div className="pp-courts-track-wrap">
          <div className="pp-courts-track" style={{ paddingLeft: 'max(28px, calc((100vw - 1240px)/2 + 28px))', paddingRight: '28px' }}>
            {courtCards.map((court) => (
              <div key={court.id} className="pp-court-card">
                <div className="pp-court-img" style={{ backgroundImage: `url(${court.img})` }}>
                  <div className="pp-court-img-overlay" />
                  <span className="pp-court-badge">{court.badge}</span>
                  <span className="pp-court-price">{court.price}</span>
                </div>
                <div className="pp-court-info">
                  <h3 className="pp-court-name">{court.name}</h3>
                  <p className="pp-court-sport">{court.sport}</p>
                  <p className="pp-court-desc">{court.desc}</p>
                  {bookingEnabled && (
                    <button
                      className="pp-court-btn"
                      onClick={() => openBooking(court.ground)}
                    >
                      {t('courtBookLabel', 'View & Book')}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── TESTIMONIALS ── */}
      {isWebsiteSectionVisible(website, "testimonials") && (
      <section className="pp-section pp-bg-cream">
        <div className="pp-container">
          <div className="pp-section-header" data-animate="fade-up">
            <p className="pp-eyebrow">
              {t('testimonialsEyebrow', 'Player Reviews')}
            </p>
            <h2 className="pp-section-title">
              {t('testimonialsTitle', 'What Players Say')}
            </h2>
          </div>
          <div className="pp-testimonials-list">
            {testimonials.map((testimonial, i) => (
              <div key={i} className="pp-testimonial-item" data-animate="fade-up" style={{ '--delay': `${i * 0.1}s` }}>
                <div className="pp-testimonial-num">0{i + 1}</div>
                <p className="pp-testimonial-text">"{testimonial.text}"</p>
                <div className="pp-testimonial-author">
                  <div className="pp-testimonial-name">{testimonial.name}</div>
                  <div className="pp-testimonial-sport">{testimonial.sport}</div>
                  <div className="pp-testimonial-stars">
                    {Array.from({ length: testimonial.rating }).map((_, si) => <span key={si} className="pp-star">★</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── FAQ ── */}
      {isWebsiteSectionVisible(website, "faqs") && (
      <section id="faq" className="pp-section pp-bg-sand">
        <div className="pp-container">
          <div className="pp-faq-grid">
            <div className="pp-faq-left" data-animate="fade-right">
              <p className="pp-eyebrow">{t('faqEyebrow', 'FAQ')}</p>
              <h2 className="pp-section-title">
                {t('faqTitle', 'Got Questions?')}
              </h2>
              <p className="pp-faq-left-desc">
                {t(
                  'faqDescription',
                  "Everything you need to know about playing at Pickle Point Nepal. Can't find your answer? Just call us — we're always happy to help.",
                )}
              </p>
              <div style={{ marginTop: '32px' }}>
                {isWebsiteSectionVisible(website, "contact") && (
                  <button className="pp-btn-primary pp-btn-sm" onClick={() => scrollTo('contact')}>
                    {t('faqContactLabel', 'Contact Us')}
                  </button>
                )}
              </div>
            </div>

            <div className="pp-faq-list" data-animate="fade-left">
              {faqs.map((faq, i) => (
                <div key={i} className={`pp-faq-item ${openFaq === i ? 'open' : ''}`}>
                  <button className="pp-faq-trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                    {faq.q}
                    <span className="pp-faq-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                    </span>
                  </button>
                  <div className="pp-faq-body">
                    <p>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── CONTACT ── */}
      {isWebsiteSectionVisible(website, "contact") && (
      <section id="contact" className="pp-section pp-bg-cream">
        <div className="pp-container">
          <div className="pp-section-header" data-animate="fade-up">
            <p className="pp-eyebrow">
              {t('contactEyebrow', 'Get In Touch')}
            </p>
            <h2 className="pp-section-title">
              {t('contactTitle', 'Book Your Court')}
            </h2>
            <p className="pp-section-sub">
              {t(
                'contactDescription',
                "Ready to play? Send us a message or call directly and we'll get you on court as soon as possible.",
              )}
            </p>
          </div>

          <div className="pp-contact-wrapper" data-animate="fade-up" style={{ '--delay': '0.1s' }}>
            <div className="pp-contact-grid">

              {/* Left: Coral Info Panel */}
              <div className="pp-contact-left">
                <div>
                  <h3 className="pp-contact-big-title">
                    {t('contactPanelTitleLineOne', "Let's")}
                    <br />
                    {t('contactPanelTitleLineTwo', 'Play.')}
                  </h3>
                  <p className="pp-contact-desc">
                    {t(
                      'contactPanelDescription',
                      "Two premium covered courts in the heart of Pulchowk. Walk in or book ahead — we're here for you.",
                      venue?.shortDescription,
                    )}
                  </p>
                </div>
                <div className="pp-contact-items">
                  <div className="pp-contact-item">
                    <div className="pp-contact-item-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <div>
                      <div className="pp-contact-item-label">{t('locationLabel', 'Location')}</div>
                      <div className="pp-contact-item-val">{venueAddress}</div>
                    </div>
                  </div>
                  <div className="pp-contact-item">
                    <div className="pp-contact-item-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </div>
                    <div>
                      <div className="pp-contact-item-label">{t('phoneLabel', 'Phone')}</div>
                      <div className="pp-contact-item-val">{venuePhone}</div>
                    </div>
                  </div>
                  <div className="pp-contact-item">
                    <div className="pp-contact-item-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div>
                      <div className="pp-contact-item-label">{t('hoursLabel', 'Hours')}</div>
                      <div className="pp-contact-item-val">
                        {displayHours} {t('dailyLabel', 'Daily')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="pp-contact-right">
                <h3 className="pp-contact-form-title">{t('formTitle', 'Send a Message')}</h3>
                <form className="pp-contact-form" onSubmit={handleSubmit}>
                  <PublicContactHoneypot value={formData.company} onChange={handleInput} />
                  <div className="pp-form-row">
                    <div className="pp-form-group">
                      <label htmlFor="pp-name">{t('formNameLabel', 'Your Name')}</label>
                      <input id="pp-name" name="name" type="text" autoComplete="name" maxLength={100} placeholder={t('formNamePlaceholder', 'Aarav Shrestha')} value={formData.name} onChange={handleInput} required />
                    </div>
                    <div className="pp-form-group">
                      <label htmlFor="pp-phone">{t('formPhoneLabel', 'Phone')}</label>
                      <PublicContactPhoneField
                        id="pp-phone"
                        value={formData.phone}
                        countryCode={formData.phoneCountryCode}
                        onChange={handleInput}
                        placeholder={t('formPhonePlaceholder', '+977 98XX XXXXXX')}
                      />
                    </div>
                  </div>
                  <div className="pp-form-group">
                    <label htmlFor="pp-email">{t('formEmailLabel', 'Email Address')}</label>
                    <input id="pp-email" name="email" type="email" autoComplete="email" maxLength={254} placeholder={t('formEmailPlaceholder', 'you@example.com')} value={formData.email} onChange={handleInput} />
                  </div>
                  <div className="pp-form-group">
                    <label htmlFor="pp-message">{t('formMessageLabel', 'Message')}</label>
                    <textarea id="pp-message" name="message" rows={4} maxLength={1200} placeholder={t(
                        'formMessagePlaceholder',
                        "Tell us when you'd like to book, how many players, or any questions...",
                      )} value={formData.message} onChange={handleInput} required />
                  </div>
                  <button type="submit" className="pp-btn-submit" disabled={contactSubmitting}>
                    {contactSubmitting ? 'Sending…' : t('formSubmitLabel', 'Send Message')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </button>
                  <PublicContactEnquiryStatus submitting={contactSubmitting} feedback={contactFeedback} comfortableSpacing />
                </form>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="pp-map-section" data-animate="fade-up" style={{ '--delay': '0.2s' }}>
            <p className="pp-map-label">
              {t('mapLabel', '📍 Find Us On The Map')}
            </p>
            <div className="pp-map-wrap" style={{ position: 'relative' }}>
              <iframe
                title={t(
                  'mapTitle',
                  'Pickle Point Nepal Location',
                  venue?.name,
                )}
                className="pp-map"
                loading="lazy"
                src={mapEmbedUrl}
                allowFullScreen
              />
              <OpenMapsLink
                url={venue?.googleMapsUrl}
                latitude={venue?.location?.latitude}
                longitude={venue?.location?.longitude}
                venueName={venue?.name || 'Pickle Point Nepal'}
                label={t('openMapsLabel', 'Open in Maps')}
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── APP CTA ── */}
      {isWebsiteSectionVisible(website, "app") && (
      <section className="pp-cta-section">
        <div className="pp-cta-bg" aria-hidden="true" />
        <div className="pp-container">
          <div className="pp-cta-inner">
            <div data-animate="fade-right">
              <p className="pp-eyebrow" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {t('appEyebrow', 'ONGROUND App')}
              </p>
              <h2 className="pp-cta-title">
                {t('appTitleLineOne', 'Book Courts')}
                <br />
                <span>{t('appTitleLineTwo', 'On the Go.')}</span>
              </h2>
              <p className="pp-cta-desc">
                {t(
                  'appDescription',
                  'Download the ONGROUND app to book Pickle Point courts in seconds, manage your schedule, and connect with the pickleball community in Nepal.',
                )}
              </p>
              <div className="pp-cta-actions">
                <a
                  href={appStoreUrl}
                  className="pp-cta-store-btn"
                >
                  <OngroundStoreIcon
                    platform="appStore"
                    className="pp-cta-store-icon"
                    monochromeColor="#ffffff"
                  />
                  <div>
                    <span className="pp-cta-store-label">{t('appStoreSmallLabel', 'Download on the')}</span>
                    <span className="pp-cta-store-name">{t('appStoreMainLabel', 'App Store')}</span>
                  </div>
                </a>
                <a
                  href={googlePlayUrl}
                  className="pp-cta-store-btn"
                >
                  <OngroundStoreIcon
                    platform="googlePlay"
                    className="pp-cta-store-icon"
                  />
                  <div>
                    <span className="pp-cta-store-label">{t('googlePlaySmallLabel', 'Get it on')}</span>
                    <span className="pp-cta-store-name">{t('googlePlayMainLabel', 'Google Play')}</span>
                  </div>
                </a>
              </div>
            </div>
            <div className="pp-cta-image" data-animate="fade-left">
              <div className="pp-cta-img-glow" />
              <img
                src={appImage}
                alt={t('appImageAlt', 'ONGROUND App')}
                className="pp-cta-img"
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER ── */}
      <footer className="pp-footer">
        <div className="pp-container">
          <div className="pp-footer-grid">
            <div>
              <button className="pp-logo" onClick={() => scrollTo('hero')} style={{ marginBottom: '0' }}>
                <div className="pp-logo-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/>
                  </svg>
                </div>
                <div>
                  <span className="pp-logo-text" style={{ color: 'white' }}>
                    {t(
                  'logoPrimaryText',
                  venueBrand.primary,
                  venueBrand.primary,
                )}
                <span>
                  {t(
                    'logoHighlightText',
                    venueBrand.highlighted,
                    venueBrand.highlighted,
                  )}
                </span>
                  </span>
                  <span className="pp-logo-sub">
                    {t(
                      'logoSubText',
                      'Nepal · Pulchowk',
                      `${venueCountry} · ${venueCity}`,
                    )}
                  </span>
                </div>
              </button>
              <p className="pp-footer-tagline">
                {t(
                  'footerTagline',
                  "Lalitpur's premier pickleball destination. Two covered courts, one great community.",
                  venue?.shortDescription,
                )}
              </p>
            </div>
            <div>
              <h4 className="pp-footer-col-title">{t('footerQuickLinksTitle', 'Quick Links')}</h4>
              <ul className="pp-footer-links">
                {navigation.map((link) => (
                  <li key={link.id}>
                    <button
                      className="pp-footer-link-btn"
                      onClick={() => scrollTo(link.id)}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="pp-footer-col-title">{t('footerContactTitle', 'Contact Info')}</h4>
              <ul className="pp-footer-links">
                <li>
                  <span className="pp-footer-link-btn" style={{ cursor: 'default' }}>
                    {t(
                      'footerLocationValue',
                      'Pulchowk, Lalitpur',
                      apiVenueAddress,
                    )}
                  </span>
                </li>
                <li>
                  <span className="pp-footer-link-btn" style={{ cursor: 'default' }}>
                    {venuePhone}
                  </span>
                </li>
                <li>
                  <span className="pp-footer-link-btn" style={{ cursor: 'default' }}>
                    {displayHours}
                  </span>
                </li>
              </ul>
            </div>
            {hasVenueSocialLinks && (
              <div>
              <h4 className="pp-footer-col-title">{t('footerSocialTitle', 'Socials')}</h4>
              <ul className="pp-footer-links">
                {instagramUrl && (
                  <li>
                    <a
                      href={instagramUrl}
                      className="pp-footer-link-btn"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                      {t('instagramLabel', 'Instagram')}
                    </a>
                  </li>
                )}
                {facebookUrl && (
                  <li>
                    <a
                      href={facebookUrl}
                      className="pp-footer-link-btn"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                      {t('facebookLabel', 'Facebook')}
                    </a>
                  </li>
                )}
                {tiktokUrl && (
                  <li>
                    <a
                      href={tiktokUrl}
                      className="pp-footer-link-btn"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path></svg>
                      {t('tiktokLabel', 'TikTok')}
                    </a>
                  </li>
                )}
                {whatsappUrl && (
                  <li>
                    <a
                      href={whatsappUrl}
                      className="pp-footer-link-btn"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                      {t('whatsappLabel', 'WhatsApp')}
                    </a>
                  </li>
                )}
              </ul>
              </div>
            )}
          </div>
          <div className="pp-footer-bottom">
            <p className="pp-footer-copy">
              © {new Date().getFullYear()} {cleanText(venue?.name) || 'Pickle Point Nepal'}
              {t('footerCopyrightSuffix', '. All rights reserved.')}
            </p>
            <a
              className="pp-footer-powered"
              href="https://getonground.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit Onground website"
            >
              {t('footerPoweredByLabel', 'Powered by')}{' '}
              <img
                src={poweredLogo}
                alt={t('poweredLogoAlt', 'ONGROUND')}
                style={{ height: '22px', opacity: 0.8 }}
              />
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}
